/**
 * DefaultThemeV2Mapper Implementation
 *
 * Default implementation of ThemeV2Mapper interface that applies
 * theme settings to all 13 V2 StepKinds.
 *
 * Uses V2 paths (.appearance.*) instead of V1 paths (.styles.*).
 * Uses align ('start'|'center'|'end') instead of textAlign ('left'|'center'|'right').
 *
 * @module themes/mappers
 */

import type { ThemeV2Mapper, ThemeV2Mapping } from './ThemeV2Mapper';
import { getMappingGroup, type ThemeApplyGroup } from './themeApplyGroups';
import { addAlpha50, type ThemeV2, type ThemeV2ButtonLayoutSettings } from '../types/themesV2.types';
import type {
  StepKind,
  FlowDocument,
  StepContent,
  Backdrop,
  HeaderBar,
  ActionBar,
} from '@segmently/schema-models/v2';

import { deepEqual, getValueByPath, setValueByPath } from './objectPath';

import {
  COMMON_V2_MAPPINGS,
  COPY_BLOCK_V2_MAPPINGS,
  SELECTION_V2_MAPPINGS,
  INPUT_V2_MAPPINGS,
  SPECIAL_V2_MAPPINGS,
} from './mappingTablesV2';
import { templateTileKey } from './templateIdentity';

// ============================================================================
// Array Path Handler
// ============================================================================

/**
 * Apply mapping value to array items.
 * Handles paths with [] notation (e.g., 'options.items[].title.appearance.color')
 */
function applyToArrayItems(
  content: Record<string, unknown>,
  mapping: ThemeV2Mapping,
  value: unknown
): void {
  const target = mapping.target;
  const arrayMatch = target.match(/^(.+)\[\]\.(.+)$/);

  if (!arrayMatch) {
    setValueByPath(content, target, value);
    return;
  }

  const [, arrayPath, itemPath] = arrayMatch;
  const array = getValueByPath(content, arrayPath);

  if (!Array.isArray(array)) return;

  for (const item of array) {
    if (item && typeof item === 'object') {
      setValueByPath(item as Record<string, unknown>, itemPath, value);
    }
  }
}

/**
 * Deep clone an object using structuredClone
 */
function deepClone<T>(obj: T): T {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  return structuredClone(obj);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function deleteValueByPath(obj: Record<string, unknown>, path: string): void {
  const parts = path.split('.');
  let current: Record<string, unknown> = obj;

  for (let index = 0; index < parts.length - 1; index += 1) {
    const next = current[parts[index]];
    if (!isRecord(next)) return;
    current = next;
  }

  delete current[parts[parts.length - 1]];
}

/**
 * Write the exact target owned by a Theme setting.
 *
 * A template's current value is deliberately not part of the decision. Theme
 * Settings own their declared target paths; values outside those paths remain
 * untouched.
 */
function writeThemeOwnedPath(
  target: Record<string, unknown>,
  path: string,
  nextValue: unknown,
  opts?: { deleteWhenNull?: boolean }
): void {
  if (nextValue === undefined || (opts?.deleteWhenNull && nextValue === null)) {
    deleteValueByPath(target, path);
  } else {
    setValueByPath(target, path, deepClone(nextValue));
  }
}

function writeThemeOwnedArrayItems(
  content: Record<string, unknown>,
  mapping: ThemeV2Mapping,
  nextValue: unknown,
  opts?: { deleteWhenNull?: boolean }
): void {
  const arrayMatch = mapping.target.match(/^(.+)\[\]\.(.+)$/);
  if (!arrayMatch) {
    writeThemeOwnedPath(content, mapping.target, nextValue, opts);
    return;
  }

  const [, arrayPath, itemPath] = arrayMatch;
  const array = getValueByPath(content, arrayPath);
  if (!Array.isArray(array)) return;

  for (const item of array) {
    if (isRecord(item)) {
      writeThemeOwnedPath(item, itemPath, nextValue, opts);
    }
  }
}

/**
 * Force only changed leaves when no authored path is available. A
 * discriminated object is replaced atomically when its `kind` changes.
 */
function writeChangedLeaves(
  target: Record<string, unknown>,
  targetPath: string,
  previousValue: unknown,
  nextValue: unknown
): void {
  if (deepEqual(previousValue, nextValue)) return;

  if (
    isRecord(previousValue) &&
    isRecord(nextValue) &&
    deepEqual(previousValue.kind, nextValue.kind)
  ) {
    const currentValue = getValueByPath(target, targetPath);
    if (!isRecord(currentValue)) {
      writeThemeOwnedPath(target, targetPath, nextValue);
      return;
    }

    const keys = new Set([
      ...Object.keys(previousValue),
      ...Object.keys(nextValue),
    ]);
    for (const key of keys) {
      writeChangedLeaves(
        target,
        `${targetPath}.${key}`,
        previousValue[key],
        nextValue[key]
      );
    }
    return;
  }

  writeThemeOwnedPath(target, targetPath, nextValue);
}

function pathsOverlap(left: string, right: string): boolean {
  return (
    left === right ||
    left.startsWith(`${right}.`) ||
    right.startsWith(`${left}.`)
  );
}

// ============================================================================
// All V2 Mappings Combined
// ============================================================================

export const ALL_V2_MAPPINGS: ThemeV2Mapping[] = [
  ...COMMON_V2_MAPPINGS,
  ...COPY_BLOCK_V2_MAPPINGS,
  ...SELECTION_V2_MAPPINGS,
  ...INPUT_V2_MAPPINGS,
  ...SPECIAL_V2_MAPPINGS,
];

// ============================================================================
// DefaultThemeV2Mapper Implementation
// ============================================================================

/**
 * Default ThemeV2Mapper implementation.
 *
 * Provides mapping for all 13 V2 StepKinds using the mapping tables
 * defined in mappingTablesV2/*.ts files.
 */
export const defaultThemeV2Mapper: ThemeV2Mapper = {
  id: 'default-v2',
  name: 'Default Theme V2 Mapper',
  version: '1.0.0',

  /**
   * Get all mappings for a specific StepKind
   */
  getMappingsForStepKind(stepKind: StepKind): ThemeV2Mapping[] {
    return ALL_V2_MAPPINGS.filter((mapping) => {
      if (!mapping.condition) return true;
      return mapping.condition(stepKind);
    });
  },

  /**
   * Apply theme to screen-level objects (backdrop, headerBar, actionBar)
   *
   * Propagates colors/fonts to header buttons and action bar buttons.
   * Used by ThemeV2EditorView when colors/fonts change.
   */
  applyToBasicScreen(
    theme: ThemeV2,
    opts?: { mode?: 'cascade' | 'seed' }
  ): {
    background: Backdrop;
    headerBar: HeaderBar;
    actionBar: ActionBar;
  } {
    const { colors, fonts, layoutSettings } = theme;
    // Seed mode (direct header/footer edit): only fill absent colors, never
    // clobber a value the user just set. Cascade mode (color/font change):
    // overwrite so the palette re-cascades.
    const seed = opts?.mode === 'seed';
    const putColor = (obj: Record<string, unknown>, key: string, value: unknown) => {
      if (!seed || obj[key] == null) obj[key] = value;
    };

    const background = deepClone(theme.background);
    const headerBar = deepClone(theme.headerBar);
    const actionBar = deepClone(theme.actionBar);

    // Helper to safely get/set appearance on TapTargetContent (union type)
    // Creates appearance object if it doesn't exist yet (e.g. after button toggle ON)
    const setContentAppearance = (
      content: unknown,
      props: Record<string, unknown>
    ) => {
      if (!content || typeof content !== 'object') return;
      const c = content as Record<string, unknown>;
      if (!c.appearance || typeof c.appearance !== 'object') {
        c.appearance = {};
      }
      const appearance = c.appearance as Record<string, unknown>;
      for (const [key, value] of Object.entries(props)) {
        putColor(appearance, key, value);
      }
    };

    const mergeTargetAppearance = (
      target: unknown,
      props: Record<string, unknown>
    ) => {
      if (!target || typeof target !== 'object') return;
      const t = target as Record<string, unknown>;
      if (!t.appearance || typeof t.appearance !== 'object') {
        t.appearance = {};
      }
      const appearance = t.appearance as Record<string, unknown>;
      for (const [key, value] of Object.entries(props)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          appearance[key] = {
            ...((appearance[key] && typeof appearance[key] === 'object')
              ? appearance[key] as Record<string, unknown>
              : {}),
            ...(value as Record<string, unknown>),
          };
        } else {
          appearance[key] = value;
        }
      }
    };

    const applyButtonLayout = (
      button: unknown,
      settings: ThemeV2ButtonLayoutSettings | undefined
    ) => {
      if (!settings) return;
      const props: Record<string, unknown> = {};
      if (settings.radius !== undefined) {
        props.borders = { radius: { uniform: settings.radius } };
      }
      if (settings.height !== undefined) {
        props.dimensions = { height: settings.height };
      }
      if (settings.insets !== undefined) {
        props.insets = settings.insets;
      }
      mergeTargetAppearance(button, props);
    };

    // Apply colors/fonts to actionBar primary button
    if (actionBar.primary) {
      if (!actionBar.primary.appearance) actionBar.primary.appearance = {};
      putColor(
        actionBar.primary.appearance as Record<string, unknown>,
        'backgroundColor',
        colors.buttonsBackground
      );
      applyButtonLayout(actionBar.primary, layoutSettings?.actionBar?.primaryButton);
      setContentAppearance(actionBar.primary.content, {
        color: colors.buttonsTitle,
        fontFamily: fonts.main.fontFamily,
      });
    }

    // Apply to secondary button
    if (actionBar.secondary) {
      applyButtonLayout(actionBar.secondary, layoutSettings?.actionBar?.secondaryButton);
      setContentAppearance(actionBar.secondary.content, {
        color: colors.buttonsBackground,
        fontFamily: fonts.main.fontFamily,
      });
    }

    if (layoutSettings?.actionBar?.insets) {
      mergeTargetAppearance(actionBar, { insets: layoutSettings.actionBar.insets });
    }

    // Apply to header skip/back/close
    if (headerBar.skip) {
      setContentAppearance(headerBar.skip.content, {
        color: colors.titlesSubtitles,
        fontFamily: fonts.main.fontFamily,
      });
    }

    if (headerBar.back) {
      setContentAppearance(headerBar.back.content, {
        color: colors.titlesSubtitles,
      });
    }

    if (headerBar.close) {
      setContentAppearance(headerBar.close.content, {
        color: colors.titlesSubtitles,
      });
    }

    // Apply theme accent color to progressIndicator (v2.13.0 flat fields)
    if (headerBar.progressIndicator) {
      const pi = headerBar.progressIndicator as Record<string, unknown>;
      putColor(pi, 'activeColor', colors.buttonsBackground);
      putColor(pi, 'trackColor', addAlpha50(colors.titlesSubtitles));
    }

    return { background, headerBar, actionBar };
  },

  /**
   * Apply theme to a single screen's StepContent.
   *
   * Applies all V2 mappings to the content object using dot-path notation.
   * Handles array paths (items[]) for options items, carousel slides, etc.
   *
   * When `groups` is provided, only mappings belonging to those apply groups
   * are used (fail-closed: mappings with an unknown group are excluded).
   * When omitted, all mappings apply (existing behavior).
   */
  applyToStepContent(
    theme: ThemeV2,
    stepKind: StepKind,
    content: StepContent,
    groups?: ThemeApplyGroup[]
  ): StepContent {
    const cloned = deepClone(content);
    const contentAsRecord = cloned as unknown as Record<string, unknown>;
    let mappings = this.getMappingsForStepKind(stepKind);
    if (groups) {
      mappings = mappings.filter((mapping) => {
        const group = getMappingGroup(mapping);
        return group !== null && groups.includes(group);
      });
    }

    for (const mapping of mappings) {
      const sourceValue = getValueByPath(theme, mapping.source);
      if (sourceValue === undefined) continue;

      const finalValue = mapping.transform
        ? mapping.transform(sourceValue)
        : sourceValue;
      if (finalValue === undefined) continue;

      if (mapping.target.includes('[]')) {
        applyToArrayItems(contentAsRecord, mapping, finalValue);
      } else {
        setValueByPath(contentAsRecord, mapping.target, finalValue);
      }
    }

    return cloned;
  },

  /**
   * Apply only fields changed between two theme revisions.
   *
   * Every target owned by the changed Theme field is updated regardless of
   * its current template value. Unrelated target paths remain unchanged.
   */
  applyThemeDeltaToFlowDocument(
    previousTheme: ThemeV2,
    nextTheme: ThemeV2,
    flowDocument: FlowDocument,
    opts?: { changedThemePath?: string; selectedTemplateKeys?: string[] }
  ): FlowDocument {
    const cloned = deepClone(flowDocument);
    const changedThemePath = opts?.changedThemePath;
    const selectedTemplateKeys = opts?.selectedTemplateKeys === undefined
      ? undefined
      : new Set(opts.selectedTemplateKeys);

    const finalMappingValue = (
      theme: ThemeV2,
      mappings: ThemeV2Mapping[]
    ): unknown => {
      let finalValue: unknown;

      // Match applyToStepContent: undefined sources are skipped and the last
      // mapping with a defined source wins. Deleting a higher-priority source
      // therefore reveals the next defined fallback for the same target.
      for (const mapping of mappings) {
        const sourceValue = getValueByPath(theme, mapping.source);
        if (sourceValue === undefined) continue;
        const candidate = mapping.transform
          ? mapping.transform(sourceValue)
          : sourceValue;
        if (candidate === undefined) continue;
        finalValue = candidate;
      }

      return finalValue;
    };

    const chromeThemePath = (target: string): string | undefined => {
      if (target === 'header') return 'headerBar';
      if (target.startsWith('header.')) {
        return `headerBar.${target.slice('header.'.length)}`;
      }
      if (target === 'actionBar' || target.startsWith('actionBar.')) {
        return target;
      }
      return undefined;
    };

    const restoreChromeParentIfNeeded = (
      content: Record<string, unknown>,
      target: string
    ): void => {
      const [contentRegion, member] = target.split('.');
      if (contentRegion !== 'header' && contentRegion !== 'actionBar') return;

      const themeRegion = contentRegion === 'header' ? 'headerBar' : 'actionBar';
      const currentRegion = getValueByPath(content, contentRegion);
      const nextRegion = getValueByPath(nextTheme, themeRegion);

      if (!isRecord(currentRegion) && isRecord(nextRegion)) {
        setValueByPath(content, contentRegion, deepClone(nextRegion));
        return;
      }

      if (!member) return;
      const currentMember = getValueByPath(content, `${contentRegion}.${member}`);
      const nextMember = getValueByPath(nextTheme, `${themeRegion}.${member}`);
      if (!isRecord(currentMember) && isRecord(nextMember)) {
        setValueByPath(
          content,
          `${contentRegion}.${member}`,
          deepClone(nextMember)
        );
      }
    };

    const applyDirectRegionDelta = (
      content: Record<string, unknown>,
      themePrefix: 'background' | 'headerBar' | 'actionBar',
      contentPrefix: 'canvas.background' | 'header' | 'actionBar'
    ): void => {
      if (!changedThemePath || !pathsOverlap(changedThemePath, themePrefix)) {
        return;
      }

      const relativePath = changedThemePath === themePrefix
        ? ''
        : changedThemePath.slice(themePrefix.length + 1);
      const targetPath = relativePath
        ? `${contentPrefix}.${relativePath}`
        : contentPrefix;
      const previousRegion = getValueByPath(previousTheme, themePrefix);
      const nextRegion = getValueByPath(nextTheme, themePrefix);
      const currentRegion = getValueByPath(content, contentPrefix);
      const previousKind = isRecord(previousRegion) ? previousRegion.kind : undefined;
      const nextKind = isRecord(nextRegion) ? nextRegion.kind : undefined;
      const currentMissingDiscriminant =
        isRecord(nextRegion) &&
        'kind' in nextRegion &&
        (!isRecord(currentRegion) || !('kind' in currentRegion));

      // A discriminated region must move as one valid object. Updating only
      // `kind` can otherwise combine a new body with the old discriminator.
      if (
        relativePath === '' ||
        (themePrefix === 'background' && relativePath === 'kind') ||
        currentMissingDiscriminant ||
        !deepEqual(previousKind, nextKind) ||
        currentRegion === undefined
      ) {
        writeThemeOwnedPath(content, contentPrefix, nextRegion);
        return;
      }

      if (themePrefix !== 'background' && relativePath.includes('.')) {
        restoreChromeParentIfNeeded(content, targetPath);
      }

      writeThemeOwnedPath(
        content,
        targetPath,
        getValueByPath(nextTheme, changedThemePath)
      );
    };

    for (const screen of Object.values(cloned.screens)) {
      if (
        selectedTemplateKeys !== undefined &&
        !selectedTemplateKeys.has(templateTileKey(screen.screenType, screen.subtype))
      ) {
        continue;
      }

      const content = screen.content as unknown as Record<string, unknown>;
      const supportsChrome =
        screen.screenType !== 'Paywall' &&
        screen.screenType !== 'WebEmbed' &&
        screen.screenType !== 'FlexibleLayout';

      // Group duplicate targets, then resolve the last mapping whose source is
      // defined for each theme revision (the same precedence as full apply).
      const mappingsByTarget = new Map<string, ThemeV2Mapping[]>();
      for (const mapping of this.getMappingsForStepKind(screen.screenType)) {
        const mappings = mappingsByTarget.get(mapping.target) ?? [];
        mappings.push(mapping);
        mappingsByTarget.set(mapping.target, mappings);
      }

      for (const [target, mappings] of mappingsByTarget) {
        const mapping = mappings[mappings.length - 1];
        if (
          !supportsChrome &&
          (target.startsWith('header.') || target.startsWith('actionBar.'))
        ) {
          continue;
        }
        if (
          changedThemePath &&
          !mappings.some(({ source }) => pathsOverlap(changedThemePath, source))
        ) {
          continue;
        }

        const structuralPath = chromeThemePath(target);
        const previousValue = structuralPath
          ? getValueByPath(previousTheme, structuralPath)
          : finalMappingValue(previousTheme, mappings);
        const nextValue = structuralPath
          ? getValueByPath(nextTheme, structuralPath)
          : finalMappingValue(nextTheme, mappings);
        if (deepEqual(previousValue, nextValue)) continue;

        if (structuralPath) {
          restoreChromeParentIfNeeded(content, target);
        }
        writeThemeOwnedArrayItems(content, mapping, nextValue, {
          deleteWhenNull: target === 'canvas.webViewBackgroundColor',
        });
      }

      if (changedThemePath) {
        applyDirectRegionDelta(content, 'background', 'canvas.background');
        if (supportsChrome) {
          applyDirectRegionDelta(content, 'headerBar', 'header');
          applyDirectRegionDelta(content, 'actionBar', 'actionBar');
        }
      } else {
        writeChangedLeaves(
          content,
          'canvas.background',
          previousTheme.background,
          nextTheme.background
        );
        if (supportsChrome) {
          writeChangedLeaves(
            content,
            'header',
            previousTheme.headerBar,
            nextTheme.headerBar
          );
          writeChangedLeaves(
            content,
            'actionBar',
            previousTheme.actionBar,
            nextTheme.actionBar
          );
        }
      }
    }

    return cloned;
  },

  /**
   * Apply theme to entire FlowDocument.
   *
   * For each screen in the FlowDocument:
   * 1. Apply screen-specific content mappings (title, subtitle, items, etc.)
   * 2. Set canvas.background from theme.background
   * 3. Set header from theme.headerBar
   * 4. Set actionBar from theme.actionBar
   *
   * Note: Uses theme.headerBar/actionBar/background directly.
   * Color propagation is handled by ThemeV2EditorView when colors/fonts change.
   *
   * This is a forceful projection used for initial templates and explicit
   * whole-theme projection. It does not infer per-template overrides from the
   * values already stored in the target document.
   */
  applyToFlowDocument(theme: ThemeV2, flowDocument: FlowDocument): FlowDocument {
    const cloned = deepClone(flowDocument);

    for (const screenId of Object.keys(cloned.screens)) {
      const screen = cloned.screens[screenId];

      // Apply content-level mappings
      screen.content = this.applyToStepContent(
        theme,
        screen.screenType,
        screen.content
      );

      // Apply screen-level overrides from theme
      screen.content.canvas.background = deepClone(theme.background);

      // Apply header/actionBar (skip for Paywall, WebEmbed, FlexibleLayout which manage their own)
      if (screen.screenType !== 'Paywall' && screen.screenType !== 'WebEmbed' && screen.screenType !== 'FlexibleLayout') {
        screen.content.header = deepClone(theme.headerBar);
        screen.content.actionBar = deepClone(theme.actionBar);
      }
    }

    return cloned;
  },
};
